import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Briefcase } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { waEventLink, waQuoteLink, IMAGES } from "@/data/site";

const services = [
  "Preparazione di buffet",
  "Piatti in formato monoporzione",
  "Focacce e rustici",
  "Primi piatti",
  "Secondi e contorni",
  "Menù tradizionali salentini",
  "Preparazioni per feste private",
  "Soluzioni per aziende e associazioni",
  "Confezionamento da asporto",
  "Trasporto, quando previsto dal preventivo",
];

const fieldCls = "bg-transparent border border-charred/20 rounded-md focus-visible:ring-terracotta text-charred placeholder:text-charred/40";

const empty = {
  nome: "", telefono: "", email: "", tipo_evento: "", data_evento: "", partecipanti: "",
  luogo: "", menu_desiderato: "", trasporto: false, allergie: "", note: "", consenso: false, website: "",
};

export default function Eventi() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso) {
      toast.error("Devi autorizzare il trattamento dei dati personali.");
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success("Richiesta inviata! Ti ricontatteremo per la proposta su misura.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="eventi" className="py-24 md:py-32 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionHeading
              overline="Eventi e buffet"
              title={<>SAPORÉ per i tuoi <span className="italic text-terracotta">momenti speciali</span></>}
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-charred/70 leading-relaxed">
                Prepariamo proposte gastronomiche per feste, riunioni, eventi aziendali e occasioni private, con menù personalizzati in base al numero degli invitati, alla tipologia di evento e alle necessità organizzative.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {services.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm md:text-base text-charred/80">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" aria-hidden="true" />
                    {s}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <a href={waQuoteLink} target="_blank" rel="noopener noreferrer" data-testid="eventi-quote-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300">
                  <MessageCircle size={16} /> Richiedi un preventivo
                </a>
                <a href={waEventLink} target="_blank" rel="noopener noreferrer" data-testid="eventi-whatsapp-btn"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:text-terracotta-dark transition-colors">
                  Oppure inviaci i dettagli dell'evento su WhatsApp
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.2}>
              <div className="noise-frame overflow-hidden rounded-[4px] aspect-[4/3] group">
                <img src={IMAGES.buffet} alt="Buffet mediterraneo per eventi"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 bg-sand rounded-lg p-8 md:p-12" data-testid="quote-form">
            <h3 className="font-serif text-3xl md:text-4xl text-charred">Raccontaci il tuo evento</h3>
            <p className="mt-2 text-sm text-charred/60">Compila il modulo e ti ricontatteremo per preparare una proposta su misura.</p>
            <p className="mt-3 text-sm text-charred/80 bg-terracotta/10 border border-terracotta/20 rounded-md px-4 py-3">
              Preferisci parlarne di persona? Vieni a trovarci in sede: riceverai suggerimenti direttamente dal nostro staff per comporre insieme il preventivo perfetto.
            </p>

            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)}
                className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <Input required placeholder="Nome e cognome *" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={fieldCls} data-testid="quote-name-input" />
              <Input required placeholder="Numero di telefono *" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={fieldCls} data-testid="quote-phone-input" />
              <Input required type="email" placeholder="Indirizzo e-mail *" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} data-testid="quote-email-input" />
              <Select value={form.tipo_evento} onValueChange={(v) => set("tipo_evento", v)} required>
                <SelectTrigger className={fieldCls} data-testid="quote-type-select"><SelectValue placeholder="Tipologia di evento *" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Festa privata">Festa privata</SelectItem>
                  <SelectItem value="Evento aziendale">Evento aziendale</SelectItem>
                  <SelectItem value="Buffet">Buffet</SelectItem>
                  <SelectItem value="Ricorrenza">Ricorrenza</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <label className="text-xs text-charred/50 mb-1 block">Data dell'evento</label>
                <Input type="date" value={form.data_evento} onChange={(e) => set("data_evento", e.target.value)} className={fieldCls} data-testid="quote-date-input" />
              </div>
              <Input placeholder="Numero di partecipanti" value={form.partecipanti} onChange={(e) => set("partecipanti", e.target.value)} className={fieldCls} data-testid="quote-guests-input" />
              <Input placeholder="Luogo dell'evento" value={form.luogo} onChange={(e) => set("luogo", e.target.value)} className={`${fieldCls} sm:col-span-2`} data-testid="quote-place-input" />
              <Textarea placeholder="Menù desiderato" value={form.menu_desiderato} onChange={(e) => set("menu_desiderato", e.target.value)} className={`${fieldCls} sm:col-span-2 min-h-[90px]`} data-testid="quote-menu-textarea" />
              <Input placeholder="Allergie o intolleranze da segnalare" value={form.allergie} onChange={(e) => set("allergie", e.target.value)} className={`${fieldCls} sm:col-span-2`} data-testid="quote-allergies-input" />
              <Textarea placeholder="Note aggiuntive" value={form.note} onChange={(e) => set("note", e.target.value)} className={`${fieldCls} sm:col-span-2 min-h-[80px]`} data-testid="quote-notes-textarea" />

              <label className="flex items-center gap-3 text-sm text-charred/80 cursor-pointer">
                <Checkbox checked={form.trasporto} onCheckedChange={(v) => set("trasporto", !!v)} data-testid="quote-transport-checkbox" />
                Necessità di trasporto
              </label>
              <label className="flex items-start gap-3 text-sm text-charred/80 cursor-pointer">
                <Checkbox checked={form.consenso} onCheckedChange={(v) => set("consenso", !!v)} data-testid="quote-consent-checkbox" />
                Autorizzo il trattamento dei dati personali *
              </label>

              <button type="submit" disabled={sending} data-testid="quote-submit-btn"
                className="sm:col-span-2 rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-semibold hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60">
                {sending ? "Invio in corso…" : "Invia la richiesta"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-olive/10 border border-olive/30 rounded-lg p-8 md:p-10 h-full">
              <div className="w-12 h-12 rounded-full bg-olive/20 grid place-items-center text-olive-dark">
                <Briefcase size={20} />
              </div>
              <h3 className="mt-5 font-serif text-2xl md:text-3xl text-charred">Servizio per aziende</h3>
              <p className="mt-3 text-sm md:text-base text-charred/70 leading-relaxed">
                Una pausa pranzo più semplice e gustosa: SAPORÉ può diventare il punto di riferimento per uffici, attività commerciali e aziende.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-charred/80">
                {["Ordini programmati", "Pasti da asporto", "Forniture per riunioni", "Buffet aziendali", "Preparazioni monoporzione", "Convenzioni personalizzate"].map((x) => (
                  <li key={x} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-olive shrink-0" aria-hidden="true" /> {x}
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-xs text-charred/50">Contattaci per valutare la soluzione più adatta alla tua attività.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
